/* eslint-disable object-shorthand */
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
    Box,
    Button,
    Card,
    CircularProgress,
    Fade,
    Grid,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// Google map autocomplete
import Autocomplete from 'react-google-autocomplete';
// settings components
import SettingsSection from 'src/components/settings/SettingsSection';
import SettingsToggleRow from 'src/components/settings/SettingsToggleRow';
// utils
import { fData } from '../../../utils/formatNumber';
// components
import { Iconify } from '../../../components';
import { useAuth } from '../../../hooks';
import useSnackbar from '../../../hooks/useCustomSnackbar';
import { FormProvider, RHFTextField, RHFUploadAvatar } from '../../../components/hook-form';
import { changeUserDetails } from '../../../redux/slices/practiceUser';
import { dispatch, useSelector } from '../../../redux/store';
import { updatePractice } from '../../../redux/slices/practice';
import { NewUserSchema } from '../../../validations/validations';
import { gmbListAccounts, gmbListLocations } from '../../../services/googleMyBusiness';
import { settingsCardStyle } from '../../../styles/SettingsMenuStyle';

// ----------------------------------------------------------------------

const inputKeys = ['Backspace', 'Tab', 'ArrowRight', 'ArrowLeft', '+'];

export default function Practice({ practiceDetails, practiceId, canEdit }) {
    const [updateImg, setUpdateImg] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [editingCard, setEditingCard] = useState(null); // null | 'address' | 'contact'
    const [savedCard, setSavedCard] = useState(null); // null | 'address' | 'contact'
    const { enqueueSnackbar } = useSnackbar();
    const { practiceUser: { results } } = useSelector(state => state.practiceUser);
    const { businessIdToPhoneMap } = useSelector(state => state.whatsappV2 || {});
    const { user } = useAuth();
    const getUserList = () => results && results?.length > 0 && results?.filter(el => el.user && el.user.id === user?.pk);

    const waApiNumber = Object.values(businessIdToPhoneMap || {})?.[0]?.[0]?.display_phone_number || '';

    const isSuperuser = getUserList()[0]?.user?.is_superuser;

    const defaultValues = useMemo(
        () => ({
            avatarUrl: practiceDetails?.logo || '',
            name: practiceDetails?.name || '',
            city: practiceDetails?.address?.city || '',
            county: practiceDetails?.address?.county || '',
            postcode: practiceDetails?.address?.postcode || '',
            address_line_1: practiceDetails?.address?.address_line_1 || '',
            address_line_2: practiceDetails?.address?.address_line_2 || '',
            email: practiceDetails?.contact?.email || '',
            website: practiceDetails?.contact?.website || '',
            whatsapp: practiceDetails?.contact?.whatsapp || '',
            phone: practiceDetails?.contact?.phone || '',
        }),
        [practiceDetails]
    );

    const methods = useForm({
        resolver: yupResolver(NewUserSchema),
        defaultValues,
    });

    const {
        reset,
        watch,
        control,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    // Track whether we've done first reset
    const [updateForm, setUpdateForm] = useState(false);

    useEffect(() => {
        if (updateForm) {
            reset(defaultValues);
            return;
        }
        setUpdateForm(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValues]);

    const values = watch();

    useEffect(() => {
        if (waApiNumber && !practiceDetails?.contact?.whatsapp) {
            setValue('whatsapp', waApiNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [waApiNumber, practiceDetails?.id]);

    useEffect(() => {
        if (!practiceId) return undefined;
        let cancelled = false;
        gmbListAccounts(practiceId)
            .then((resp) => {
                if (cancelled) return;
                const accounts = Array.isArray(resp) ? resp : resp?.results || [];
                setIsGoogleConnected(accounts.length > 0);
            })
            .catch(() => {
                if (!cancelled) setIsGoogleConnected(false);
            });
        return () => { cancelled = true; };
    }, [practiceId]);

    const onSubmit = async (data) => {
        const cardKey = editingCard;
        const detail = {
            name: data.name,
            address: {
                city: data.city,
                county: data.county,
                postcode: data.postcode,
                address_line_1: data.address_line_1,
                address_line_2: data.address_line_2
            },
            contact: {
                email: data.email,
                phone: data.phone,
                website: data.website,
                whatsapp: data.whatsapp
            }
        };
        if (updateImg) {
            detail.logo = data.avatarUrl;
        }
        setUpdateImg(false);
        setEditingCard(null);
        dispatch(changeUserDetails(detail, practiceDetails.id, 'practice'));
        // Show save feedback
        setSavedCard(cardKey);
        setTimeout(() => setSavedCard(null), 1500);
    };

    const handleSubmitCard = () => {
        handleSubmit(onSubmit)();
    };

    const handleCancelEdit = () => {
        reset(defaultValues);
        setEditingCard(null);
    };

    /**
     * handleDrop file Callback.
     */
    const handleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    setValue('avatarUrl', reader.result);
                };
                setUpdateImg(true);
            }
        },
        [setValue]
    );

    const setAddressData = (addresses, fulladdress) => {
        ClearAddressData();
        if (addresses && addresses.length > 0) {
            const address1 = fulladdress.split(',')[0];
            setValue('address_line_1', address1);
            addresses.forEach(address => {
                if (address.types.indexOf('postal_code') > -1) {
                    setValue('postcode', address.long_name);
                }
                if (address.types.indexOf('country') > -1) {
                    if (address1 === address.long_name) {
                        setValue('address_line_1', '');
                    }
                    setValue('county', address.long_name);
                }
                if (address.types.indexOf('locality') > -1 || address.types.indexOf('postal_town') > -1) {
                    if (address1.includes(address.long_name)) {
                        setValue('address_line_1', '');
                    }
                    setValue('city', address.long_name);
                }
                if (address.types.indexOf('route') > -1) {
                    if (!address1.includes(address.long_name)) {
                        setValue('address_line_2', address.long_name);
                    } else {
                        setValue('address_line_2', '');
                    }
                }
            });
        } else {
            ClearAddressData();
        }
    };

    const ClearAddressData = () => {
        setValue('postcode', '');
        setValue('address_line_2', '');
        setValue('county', '');
        setValue('city', '');
        setValue('address_line_1', '');
    };

    const handleSyncFromGoogle = async () => {
        if (!practiceId) return;
        setSyncLoading(true);
        try {
            const accountsResp = await gmbListAccounts(practiceId);
            const accounts = Array.isArray(accountsResp) ? accountsResp : accountsResp?.results || [];
            if (accounts.length === 0) {
                enqueueSnackbar('No Google Business account connected. Connect one in Integrations.', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                setSyncLoading(false);
                return;
            }
            const locationsResp = await gmbListLocations(practiceId, accounts[0].id, { page_size: 100 });
            const locations = Array.isArray(locationsResp) ? locationsResp : locationsResp?.results || locationsResp?.locations || [];
            const connectedLocations = locations.filter(loc => loc?.is_connected !== false);
            if (connectedLocations.length === 0) {
                enqueueSnackbar('No Google Business location connected.', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                setSyncLoading(false);
                return;
            }
            const ld = connectedLocations[0]?.location_data || connectedLocations[0] || {};
            const phone = ld.phoneNumbers?.primaryPhone;
            const website = ld.websiteUri;
            const addr = ld.storefrontAddress || {};
            const addrLines = addr.addressLines || [];
            if (phone) setValue('phone', phone);
            if (website) setValue('website', website);
            if (addrLines[0]) setValue('address_line_1', addrLines[0]);
            if (addrLines[1]) setValue('address_line_2', addrLines[1]);
            if (addr.locality) setValue('city', addr.locality);
            if (addr.administrativeArea) setValue('county', addr.administrativeArea);
            if (addr.postalCode) setValue('postcode', addr.postalCode);
            enqueueSnackbar('Details synced from Google Business. Review and save.', { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } catch (err) {
            enqueueSnackbar(err?.message || 'Failed to sync from Google Business', { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleToggle = (field) => (event) => {
        const { checked } = event.target;
        dispatch(updatePractice({
            id: practiceId,
            data: { preferences: { [field]: checked } },
        }));
    };

    // ── Read-only display helpers ─────────────────────────────────────

    const fullAddress = [
        values.address_line_1,
        values.address_line_2,
        values.city,
        values.county,
        values.postcode,
    ].filter(Boolean).join(', ');

    // ── Card header with optional save checkmark ──────────────────────

    const CardHeader = ({ title, cardKey }) => (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                    {title}
                </Typography>
                <Fade in={savedCard === cardKey} timeout={300}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Iconify
                            icon="eva:checkmark-circle-2-fill"
                            width={18}
                            sx={{ color: '#22c55e' }}
                        />
                    </Box>
                </Fade>
            </Box>
            {canEdit && editingCard !== cardKey && (
                <Button
                    size="small"
                    onClick={() => setEditingCard(cardKey)}
                    sx={{
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#3b82f6',
                        minWidth: 'auto',
                        p: '2px 8px',
                        borderRadius: '6px',
                    }}
                >
                    Edit
                </Button>
            )}
        </Box>
    );

    CardHeader.propTypes = {
        title: PropTypes.string.isRequired,
        cardKey: PropTypes.string.isRequired,
    };

    // ── Card edit action row ──────────────────────────────────────────

    const CardEditActions = ({ cardKey }) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            {isGoogleConnected && cardKey === 'address' && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSyncFromGoogle}
                    disabled={syncLoading}
                    startIcon={syncLoading
                        ? <CircularProgress size={14} />
                        : <Iconify icon="flat-color-icons:google" width={16} height={16} />
                    }
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                    {syncLoading ? 'Syncing...' : 'Sync from Google'}
                </Button>
            )}
            <Button
                variant="outlined"
                size="small"
                onClick={handleCancelEdit}
                sx={{ textTransform: 'none', fontWeight: 500, borderRadius: '8px' }}
            >
                Cancel
            </Button>
            <LoadingButton
                variant="contained"
                size="small"
                loading={isSubmitting}
                onClick={handleSubmitCard}
                sx={{
                    backgroundColor: '#00A76F',
                    color: '#fff',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&:hover': { backgroundColor: '#007B55' },
                }}
            >
                Save
            </LoadingButton>
        </Stack>
    );

    CardEditActions.propTypes = {
        cardKey: PropTypes.string.isRequired,
    };

    // ── Info row for read-only display ────────────────────────────────

    const InfoRow = ({ icon, label, value: val }) => {
        if (!val) return null;
        return (
            <Box display="flex" alignItems="flex-start" gap={1} sx={{ py: 0.5 }}>
                <Iconify icon={icon} width={16} height={16} sx={{ color: '#64748b', mt: '2px', flexShrink: 0 }} />
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                        {val}
                    </Typography>
                </Box>
            </Box>
        );
    };

    InfoRow.propTypes = {
        icon: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string,
    };

    InfoRow.defaultProps = {
        value: '',
    };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <SettingsSection
                id="practice-info"
                icon="solar:user-bold-duotone"
                title="Practice Information"
                description="Manage your practice name, address, and contact details"
                iconColor="#3b82f6"
            >
                {/* ── Header row: Logo + Practice Name ─────────────── */}
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Box sx={{ flexShrink: 0 }}>
                        <RHFUploadAvatar
                            name="avatarUrl"
                            maxSize={3145728}
                            onDrop={handleDrop}
                            helperText={
                                <Typography
                                    variant="caption"
                                    sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'text.secondary' }}
                                >
                                    Allowed *.jpeg, *.jpg, *.png, *.gif
                                    <br /> max size of {fData(3145728)}
                                </Typography>
                            }
                        />
                    </Box>
                    <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="h5" fontWeight={700} noWrap>
                                {values.name || 'Practice Name'}
                            </Typography>
                            {practiceDetails?.preferences?.is_accepting_nhs_patients && (
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: '6px',
                                        bgcolor: alpha('#0ea5e9', 0.1),
                                        border: `1px solid ${alpha('#0ea5e9', 0.2)}`,
                                    }}
                                >
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9' }}>
                                        NHS
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {!isSuperuser ? (
                            <Typography variant="caption" color="text.secondary">
                                Practice name and email can only be edited by super admins
                            </Typography>
                        ) : (
                            <Box sx={{ mt: 1, maxWidth: 400 }}>
                                <RHFTextField
                                    name="name"
                                    label="Practice name"
                                    required
                                    size="small"
                                />
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ── Two-column cards: Address + Contact ────────────── */}
                <Grid container spacing={3} mb={3}>
                    {/* Address card */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                ...settingsCardStyle,
                                p: 2,
                                border: editingCard === 'address' ? '1px solid #3b82f6' : '1px solid transparent',
                                transition: 'border-color 0.2s',
                            }}
                        >
                            <CardHeader title="Address" cardKey="address" />

                            {editingCard === 'address' ? (
                                <Stack spacing={2}>
                                    {/* Google autocomplete */}
                                    <Box
                                        sx={{
                                            '.address-input': {
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                border: '1px solid',
                                                borderColor: 'rgba(145, 158, 171, 0.32)',
                                                fontFamily: 'inherit',
                                                fontSize: '0.875rem',
                                                outline: 'none',
                                                transition: 'border-color 0.2s',
                                                '&:hover': { borderColor: 'rgba(145, 158, 171, 0.8)' },
                                                '&:focus': { borderColor: '#1976d2', borderWidth: '2px', padding: '9px 13px' },
                                            }
                                        }}
                                    >
                                        <Controller
                                            name="address"
                                            control={control}
                                            render={({ field }) => (
                                                <Autocomplete
                                                    apiKey="AIzaSyD_7FXxCWgBUUd6922n33WKqrfRDsXXxj8"
                                                    onPlaceSelected={(place, inputRef) => {
                                                        const addresses = place.address_components;
                                                        setAddressData(addresses, inputRef.value);
                                                        field.onChange(inputRef.value);
                                                    }}
                                                    options={{
                                                        types: ['establishment', 'geocode'],
                                                        fields: ['address_components', 'formatted_address'],
                                                        componentRestrictions: { country: 'uk' }
                                                    }}
                                                    defaultValue={field.value}
                                                    className="address-input"
                                                    placeholder="Search for your practice address"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <RHFTextField name="address_line_1" label="Address line 1" required size="small" />
                                    <RHFTextField name="address_line_2" label="Address line 2" size="small" />
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12} sm={6}>
                                            <RHFTextField name="city" label="Town or city" required size="small" />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <RHFTextField name="county" label="County" required size="small" />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <RHFTextField name="postcode" label="Postcode" required size="small" />
                                        </Grid>
                                    </Grid>
                                    <CardEditActions cardKey="address" />
                                </Stack>
                            ) : (
                                <Box>
                                    {fullAddress ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                            {[values.address_line_1, values.address_line_2].filter(Boolean).join(', ')}
                                            {(values.city || values.county || values.postcode) && (
                                                <>
                                                    <br />
                                                    {[values.city, values.county, values.postcode].filter(Boolean).join(', ')}
                                                </>
                                            )}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled">
                                            No address set
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Card>
                    </Grid>

                    {/* Contact card */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                ...settingsCardStyle,
                                p: 2,
                                border: editingCard === 'contact' ? '1px solid #3b82f6' : '1px solid transparent',
                                transition: 'border-color 0.2s',
                            }}
                        >
                            <CardHeader title="Contact" cardKey="contact" />

                            {editingCard === 'contact' ? (
                                <Stack spacing={2}>
                                    <RHFTextField
                                        name="phone"
                                        label="Phone number"
                                        required
                                        size="small"
                                        type="tel"
                                        inputProps={{ maxLength: 15 }}
                                        onKeyDown={(evt) => {
                                            const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
                                            const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
                                            const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
                                            if (!(isControlC || isControlV || isAllowedKey)) {
                                                evt.preventDefault();
                                            }
                                        }}
                                    />
                                    <RHFTextField
                                        name="whatsapp"
                                        label="WhatsApp number"
                                        size="small"
                                        type="tel"
                                        inputProps={{ maxLength: 15 }}
                                        onKeyDown={(evt) => {
                                            const isControlC = (evt.ctrlKey && evt.key === 'c') || (evt.metaKey && evt.key === 'c');
                                            const isControlV = (evt.ctrlKey && evt.key === 'v') || (evt.metaKey && evt.key === 'v');
                                            const isAllowedKey = inputKeys.includes(evt.key) || (/^[0-9]*$/).test(evt.key);
                                            if (!(isControlC || isControlV || isAllowedKey)) {
                                                evt.preventDefault();
                                            }
                                        }}
                                        helperText={waApiNumber ? `WhatsApp API: ${waApiNumber}` : ''}
                                    />
                                    {isSuperuser && (
                                        <RHFTextField
                                            name="email"
                                            label="Email"
                                            required
                                            size="small"
                                        />
                                    )}
                                    {isSuperuser && (
                                        <RHFTextField
                                            name="website"
                                            label="Website URL"
                                            required
                                            size="small"
                                        />
                                    )}
                                    <CardEditActions cardKey="contact" />
                                </Stack>
                            ) : (
                                <Stack spacing={1}>
                                    <InfoRow icon="solar:phone-bold" label="Phone" value={values.phone} />
                                    <InfoRow icon="ic:baseline-whatsapp" label="WhatsApp" value={values.whatsapp} />
                                    <InfoRow icon="solar:letter-bold" label="Email" value={values.email} />
                                    <InfoRow icon="solar:global-bold" label="Website" value={values.website} />
                                    {!values.phone && !values.email && !values.website && !values.whatsapp && (
                                        <Typography variant="body2" color="text.disabled">
                                            No contact details set
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        </Card>
                    </Grid>
                </Grid>

                {/* ── Key toggles ───────────────────────────────────── */}
                <Stack spacing={0}>
                    <SettingsToggleRow
                        label="Accept Online Bookings"
                        description="Allow patients to book appointments online"
                        checked={practiceDetails?.preferences?.is_accepting_online_booking || false}
                        onChange={canEdit ? handleToggle('is_accepting_online_booking') : undefined}
                        disabled={!canEdit}
                    />
                    <SettingsToggleRow
                        label="Accept NHS Patients"
                        description="Show NHS options in forms and booking"
                        checked={practiceDetails?.preferences?.is_accepting_nhs_patients || false}
                        onChange={canEdit ? handleToggle('is_accepting_nhs_patients') : undefined}
                        disabled={!canEdit}
                    />
                </Stack>

                {/* ── Links section (hidden for now — may revisit) ─── */}
                {false && (values.website || practiceDetails?.settings?.consent_base_url || practiceDetails?.settings?.online_booking_url) && (
                    <Box mt={3}>
                        <Typography variant="subtitle2" fontWeight={700} color="#1e293b" mb={1.5}>
                            Links
                        </Typography>
                        <Grid container spacing={2}>
                            {values.website && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card sx={{ ...settingsCardStyle, p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            <Iconify icon="solar:global-bold" width={16} sx={{ color: '#3b82f6', flexShrink: 0 }} />
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                Website
                                            </Typography>
                                        </Box>
                                        <Link
                                            href={values.website.startsWith('http') ? values.website : `https://${values.website}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{ wordBreak: 'break-all', color: '#3b82f6', fontWeight: 500 }}
                                        >
                                            {values.website}
                                        </Link>
                                    </Card>
                                </Grid>
                            )}
                            {practiceDetails?.settings?.consent_base_url && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card sx={{ ...settingsCardStyle, p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            <Iconify icon="solar:document-add-bold-duotone" width={16} sx={{ color: '#8b5cf6', flexShrink: 0 }} />
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                Consent Form Base URL
                                            </Typography>
                                        </Box>
                                        <Link
                                            href={practiceDetails.settings.consent_base_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{ wordBreak: 'break-all', color: '#8b5cf6', fontWeight: 500 }}
                                        >
                                            {practiceDetails.settings.consent_base_url}
                                        </Link>
                                    </Card>
                                </Grid>
                            )}
                            {practiceDetails?.settings?.online_booking_url && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card sx={{ ...settingsCardStyle, p: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                            <Iconify icon="solar:calendar-add-bold-duotone" width={16} sx={{ color: '#10b981', flexShrink: 0 }} />
                                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                Online Booking
                                            </Typography>
                                        </Box>
                                        <Link
                                            href={practiceDetails.settings.online_booking_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="body2"
                                            sx={{ wordBreak: 'break-all', color: '#10b981', fontWeight: 500 }}
                                        >
                                            {practiceDetails.settings.online_booking_url}
                                        </Link>
                                    </Card>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}

                {/* Metadata moved to its own section at the end of PracticeDetails page */}

                {/* ── Sync from Google footer button ─────────────────── */}
                {canEdit && isGoogleConnected && editingCard === null && (
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="outlined"
                            onClick={handleSyncFromGoogle}
                            disabled={syncLoading}
                            startIcon={syncLoading
                                ? <CircularProgress size={18} />
                                : <Iconify icon="flat-color-icons:google" width={20} height={20} />
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                                px: '20px',
                                py: '8px',
                            }}
                        >
                            {syncLoading ? 'Syncing...' : 'Sync from Google'}
                        </Button>
                    </Box>
                )}
            </SettingsSection>
        </FormProvider>
    );
}

Practice.propTypes = {
    practiceDetails: PropTypes.object,
    practiceId: PropTypes.any,
    canEdit: PropTypes.bool,
};

Practice.defaultProps = {
    practiceDetails: null,
    practiceId: null,
    canEdit: false,
};
